import { TApiRes } from "@/types/api";
import { clientApi } from "@/utils/api";
import { setToken } from "@/utils/token";

import { TGetMeResDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { TLinkGoogleReqDto, TLinkGoogleResDto } from "@/api/services/main/auth/dtos/link-google.dto";
import { TLoginGoogleReqDto, TLoginGoogleResDto } from "@/api/services/main/auth/dtos/login-google.dto";
import { TLoginReqDto, TLoginResDto } from "@/api/services/main/auth/dtos/login.dto";
import { TLogoutReqDto, TLogoutResDto } from "@/api/services/main/auth/dtos/logout.dto";
import {
  TPasskeyDeleteResDto,
  TPasskeyListResDto,
  TPasskeyLoginOptionsResDto,
  TPasskeyLoginVerifyReqDto,
  TPasskeyLoginVerifyResDto,
  TPasskeyRegisterOptionsResDto,
  TPasskeyRegisterVerifyReqDto,
  TPasskeyRegisterVerifyResDto,
  TPasskeyUpdateReqDto,
  TPasskeyUpdateResDto,
} from "@/api/services/main/auth/dtos/passkey.dto";
import { TRefreshTokenReqDto, TRefreshTokenResDto } from "@/api/services/main/auth/dtos/refresh-token.dto";
import { TRegisterGoogleReqDto, TRegisterGoogleResDto } from "@/api/services/main/auth/dtos/register-google.dto";
import { TRequestResetPasswordReqDto, TRequestResetPasswordResDto } from "@/api/services/main/auth/dtos/request-reset-password.dto";
import { TResendLoginVerifyCodeReqDto, TResendLoginVerifyCodeResDto } from "@/api/services/main/auth/dtos/resend-login-verify-code.dto";
import {
  TResendRequestResetPasswordReqDto,
  TResendRequestResetPasswordResDto,
} from "@/api/services/main/auth/dtos/resend-request-reset-password.dto";
import { TResetPasswordReqDto, TResetPasswordResDto } from "@/api/services/main/auth/dtos/reset-password.dto";
import { TSendEmailToChangeUserEmailReqDto, TSendEmailToChangeUserEmailResDto } from "@/api/services/main/auth/dtos/send-email-to-change-email.dto";
import { TSetPasswordReqDto, TSetPasswordResDto } from "@/api/services/main/auth/dtos/set-password.dto";
import { TUpdateMyProfileReqDto, TUpdateMyProfileResDto } from "@/api/services/main/auth/dtos/update-my-profile.dto";
import { TUpdateUserEmailReqDto, TUpdateUserEmailResDto } from "@/api/services/main/auth/dtos/update-user-email.dto";
import { TVerifyLoginReqDto, TVerifyLoginResDto } from "@/api/services/main/auth/dtos/verify-login.dto";
import { TVerifyOAuthTokenReqDto, TVerifyOAuthTokenResDto } from "@/api/services/main/auth/dtos/verify-oauth-token.dto";
import { TVerifyResetTokenReqDto, TVerifyResetTokenResDto } from "@/api/services/main/auth/dtos/verify-reset-token.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { logout } from "@/utils/logout";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";

export const loginApi = async (payload: TLoginReqDto): Promise<TLoginResDto> => {
  const rs = await clientApi.post<TLoginResDto>("/login", payload);
  return rs.data;
};

type TAuthSessionData = {
  accessToken: string;
  refreshToken: string;
};

// The session identity (email / externalId / userId) is NOT mirrored into cookies:
// it is read back from the access token claims (see `getSessionIdentity`), so it
// can never drift from the session it belongs to.
const persistAuthSession = async (data: TAuthSessionData) => {
  await logout();
  setToken(data.accessToken, data.refreshToken);
};

export const verifyLoginApi = async (payload: TVerifyLoginReqDto): Promise<TVerifyLoginResDto> => {
  const rs = await clientApi.post<TVerifyLoginResDto>("/verify-login", payload);
  persistAuthSession(rs.data.data);
  return rs.data;
};

export const loginGoogleApi = async (payload: TLoginGoogleReqDto): Promise<TLoginGoogleResDto> => {
  const rs = await clientApi.post<TLoginGoogleResDto>("/login-google", payload);
  persistAuthSession(rs.data.data);
  return rs.data;
};

export const registerGoogleApi = async (payload: TRegisterGoogleReqDto): Promise<TRegisterGoogleResDto> => {
  const { data } = await clientApi.post<TRegisterGoogleResDto>("/register-google", payload);
  return data;
};

// Consume a single-use OAuth handoff token (e.g. admin impersonation) and persist the resulting session.
// Generic on purpose so future SSO/handoff flows can reuse the same /auth/callback page.
export const verifyOAuthTokenApi = async (payload: TVerifyOAuthTokenReqDto): Promise<TVerifyOAuthTokenResDto> => {
  const rs = await clientApi.post<TVerifyOAuthTokenResDto>("/oauth/verify-token", payload);
  persistAuthSession(rs.data.data);
  return rs.data;
};

export const resendLoginVerifyCodeApi = async (payload: TResendLoginVerifyCodeReqDto): Promise<TResendLoginVerifyCodeResDto> => {
  const rs = await clientApi.post<TResendLoginVerifyCodeResDto>("/resend-login-verify-code", payload);
  return rs.data;
};

export const requestResetPasswordApi = async (payload: TRequestResetPasswordReqDto): Promise<TRequestResetPasswordResDto> => {
  const { data } = await clientApi.post<TRequestResetPasswordResDto>("/request-reset-password", payload);
  return data;
};

export const resendRequestResetPasswordApi = async (payload: TResendRequestResetPasswordReqDto): Promise<TResendRequestResetPasswordResDto> => {
  const { data } = await clientApi.post<TResendRequestResetPasswordResDto>("/resend-request-reset-password", payload);
  return data;
};

export const fetchMeApi = async (): Promise<TGetMeResDto> => {
  const { data } = await clientApi.get<TGetMeResDto>("/get-me");
  return data;
};

// Unlink the current user's Google login. Afterward they sign in with email/password only.
export const disconnectGoogleApi = async (): Promise<TApiRes<boolean>> => {
  const { data } = await clientApi.post<TApiRes<boolean>>("/disconnect-google");
  return data;
};

// Link the current (authenticated) user's Google login. `code` comes from the Cognito Google
// OAuth redirect; the backend enforces that the resolved Google email matches the caller.
export const linkGoogleApi = async (payload: TLinkGoogleReqDto): Promise<TLinkGoogleResDto> => {
  const { data } = await clientApi.post<TLinkGoogleResDto>("/link-google", payload);
  return data;
};

export const useMe = (options?: Omit<UseQueryOptions<TGetMeResDto>, "queryKey" | "queryFn">) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER.FETCH_ME],
    queryFn: fetchMeApi,
    ...options,
  });
};

export const verifyResetTokenApi = async (payload: TVerifyResetTokenReqDto): Promise<TVerifyResetTokenResDto> => {
  const { data } = await clientApi.post<TVerifyResetTokenResDto>("/verify-reset-token", payload);
  return data;
};

export const useVerifyResetToken = (payload: Partial<TVerifyResetTokenReqDto>) => {
  const { email, resetToken, externalId } = payload;
  const enabled = Boolean(email && resetToken && externalId);

  return useQuery({
    queryKey: [QUERY_KEYS.AUTH.VERIFY_RESET_TOKEN, email, resetToken, externalId],
    queryFn: () => verifyResetTokenApi({ email: email!, resetToken: resetToken!, externalId: externalId! }),
    enabled,
    retry: false,
    staleTime: Infinity,
  });
};

export const refreshTokenApi = async (payload: TRefreshTokenReqDto): Promise<TRefreshTokenResDto> => {
  const rs = await clientApi.post<TRefreshTokenResDto>("/refresh-token", payload);
  const { data } = rs.data;
  setToken(data.accessToken, data.refreshToken);
  return rs.data;
};

export const setPasswordApi = async (payload: TSetPasswordReqDto): Promise<TSetPasswordResDto> => {
  const { data } = await clientApi.post<TSetPasswordResDto>("/set-password", payload);
  return data;
};

export const resetPasswordApi = async (payload: TResetPasswordReqDto): Promise<TResetPasswordResDto> => {
  const { data } = await clientApi.post<TResetPasswordResDto>("/reset-password", payload);
  return data;
};

export const updateMyProfileApi = async (payload: TUpdateMyProfileReqDto): Promise<TUpdateMyProfileResDto> => {
  const { data } = await clientApi.patch<TUpdateMyProfileResDto>("/update-me", payload);
  return data;
};

export const sendEmailToChangeEmailApi = async (payload: TSendEmailToChangeUserEmailReqDto): Promise<TSendEmailToChangeUserEmailResDto> => {
  const { data } = await clientApi.post<TSendEmailToChangeUserEmailResDto>("/send-email-to-change-user-email", payload);
  return data;
};

export const cancelChangeEmailApi = async (): Promise<TSendEmailToChangeUserEmailResDto> => {
  const { data } = await clientApi.post<TSendEmailToChangeUserEmailResDto>("/cancel-change-user-email");
  return data;
};

export const updateUserEmailApi = async (payload: TUpdateUserEmailReqDto): Promise<TUpdateUserEmailResDto> => {
  const { data } = await clientApi.post<TUpdateUserEmailResDto>("/update-user-email", payload);
  return data;
};

export const logoutApi = async (payload: TLogoutReqDto): Promise<TLogoutResDto> => {
  const rs = await clientApi.post<TLogoutResDto>("/logout", payload);
  return rs?.data;
};

// ---------- Passkey (WebAuthn) ----------

export const passkeyRegisterOptionsApi = async (): Promise<TPasskeyRegisterOptionsResDto> => {
  const rs = await clientApi.post<TPasskeyRegisterOptionsResDto>("/passkey/register/options");
  return rs.data;
};

export const passkeyRegisterVerifyApi = async (payload: TPasskeyRegisterVerifyReqDto): Promise<TPasskeyRegisterVerifyResDto> => {
  const rs = await clientApi.post<TPasskeyRegisterVerifyResDto>("/passkey/register/verify", payload);
  return rs.data;
};

export const passkeyListApi = async (): Promise<TPasskeyListResDto> => {
  const rs = await clientApi.get<TPasskeyListResDto>("/passkey");
  return rs.data;
};

export const usePasskeys = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.PASSKEY.LIST],
    queryFn: passkeyListApi,
  });
};

export const deletePasskeyApi = async (id: number): Promise<TPasskeyDeleteResDto> => {
  const rs = await clientApi.delete<TPasskeyDeleteResDto>(`/passkey/${id}`);
  return rs.data;
};

export const updatePasskeyApi = async (id: number, payload: TPasskeyUpdateReqDto): Promise<TPasskeyUpdateResDto> => {
  const rs = await clientApi.patch<TPasskeyUpdateResDto>(`/passkey/${id}`, payload);
  return rs.data;
};

export const passkeyLoginOptionsApi = async (): Promise<TPasskeyLoginOptionsResDto> => {
  const rs = await clientApi.post<TPasskeyLoginOptionsResDto>("/passkey/login/options");
  return rs.data;
};

export const passkeyLoginVerifyApi = async (payload: TPasskeyLoginVerifyReqDto): Promise<TPasskeyLoginVerifyResDto> => {
  const rs = await clientApi.post<TPasskeyLoginVerifyResDto>("/passkey/login/verify", payload);
  persistAuthSession(rs.data.data);
  return rs.data;
};
